import { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useConversations } from "@/hooks/useMessages";
import { useUserNotifications } from "@/hooks/useUserNotifications";
import { useAuth } from "@/contexts/AuthContext";
import Sidebar, { isOverdue, isActiveComplaint } from "@/components/hub/Sidebar";
import ChatWindow from "@/components/hub/ChatWindow";
import StatusBar from "@/components/hub/StatusBar";
import StatsDialog from "@/components/hub/StatsDialog";
import TeamDialog from "@/components/hub/TeamDialog";
import AgentStatsDialog from "@/components/hub/AgentStatsDialog";
import TemplatesDialog from "@/components/hub/TemplatesDialog";
import ShortcutsDialog from "@/components/hub/ShortcutsDialog";
import CampaignsDialog from "@/components/hub/CampaignsDialog";
import BotsDialog from "@/components/hub/BotsDialog";
import NewChatDialog from "@/components/hub/NewChatDialog";
import BookingsDialog from "@/components/hub/BookingsDialog";
import AgentBookingWizard from "@/components/hub/AgentBookingWizard";
import DbStatsDialog from "@/components/hub/DbStatsDialog";
import WelcomeMessagesDialog from "@/components/hub/WelcomeMessagesDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ShieldWarning } from "@phosphor-icons/react";

// If URL has ?view=bookings treat this tab as a dedicated Bookings page.
const isBookingsView = () =>
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).get("view") === "bookings";

export default function Dashboard() {
  const bookingsOnly = isBookingsView();
  const { user: me } = useAuth();
  useUserNotifications(me?.id, me?.full_name);
  const { contacts, messagesByPhone, typingByPhone, viewersByPhone, claimsByPhone, source, lastUpdate, loadConversationHistory, mergeContactsList, addOptimistic, replaceOptimistic, removeOptimistic } = useConversations({
    soundOn: !bookingsOnly,
  });
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [statsOpen, setStatsOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [agentStatsOpen, setAgentStatsOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [campaignsOpen, setCampaignsOpen] = useState(false);
  const [botsOpen, setBotsOpen] = useState(false);
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [bookingsOpen, setBookingsOpen] = useState(bookingsOnly);
  const [bookingPrefill, setBookingPrefill] = useState({ phone: "", name: "" });
  const [bookingWizardOpen, setBookingWizardOpen] = useState(false);
  const [bookingWizardContact, setBookingWizardContact] = useState(null);
  const [dbStatsOpen, setDbStatsOpen] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const navigate = useNavigate();

  // Re-compute overdue contacts periodically (same 30s tick as Sidebar)
  const [slaTick, setSlaTick] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setSlaTick(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (bookingsOnly) document.title = "Bookings · RodBez Whatsapp";
  }, [bookingsOnly]);

  // Per user rule 2026-02-14 — deep-link support for `?phone=...`.
  // External pages (MissedCalls → Management Feedback history, future
  // notifications/email links) can route to `/?phone=<digits>` and the
  // inbox will auto-open that customer's chat. Runs once per phone value;
  // strips the query param after applying so refresh doesn't re-trigger.
  const [deepLinkPhone] = useState(() => {
    if (typeof window === "undefined") return null;
    const p = new URLSearchParams(window.location.search).get("phone");
    return p ? String(p).replace(/[^0-9]/g, "") : null;
  });
  useEffect(() => {
    if (!deepLinkPhone || selected) return;
    setSelected(deepLinkPhone);
    try { loadConversationHistory(deepLinkPhone); } catch (_) {}
    try { api.patch(`/conversations/${deepLinkPhone}/read`); } catch (_) {}
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("phone");
      window.history.replaceState({}, "", url.pathname + (url.search ? `?${url.searchParams}` : "") + url.hash);
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkPhone]);

  const filteredContacts = useMemo(() => {
    if (bookingsOnly) return [];
    const s = search.trim().toLowerCase();
    return contacts.filter((c) => {
      // Hide contacts with no real activity (no last_timestamp) — they only
      // exist as seeded rows in /api/contacts and would clutter the inbox.
      // When the user is actively searching we DO show them, so they can be
      // looked up & opened intentionally.
      if (!s && !c.last_timestamp) return false;
      if (filter === "mine") {
        if (c.assigned_to?.id !== me?.id || c.status === "resolved") return false;
      } else if (filter === "overdue") {
        if (!isOverdue(c, slaTick)) return false;
      } else if (filter === "complaint") {
        if (!isActiveComplaint(c)) return false;
      } else if (filter === "call_request") {
        // Open call-request tickets (customer tapped "Talk to an agent"
        // / "Talk to Support" / "Direct Call Support" / "Yes, help me").
        // Cleared on close so resolved chats drop off the list.
        if (!c.call_request || c.status === "resolved") return false;
      } else if (filter === "new") {
        // "New" = unread incoming msgs that no agent has replied to yet.
        // Earlier this matched literal status="new" which inflated the
        // count to 1300+ because every legacy contact starts as "new".
        if (!(Number(c.unread) > 0)) return false;
      } else if (filter !== "all" && c.status !== filter) {
        return false;
      }
      if (!s) return true;
      return (
        (c.name || "").toLowerCase().includes(s) ||
        (c.phone || "").toLowerCase().includes(s)
      );
    });
  }, [contacts, search, filter, bookingsOnly, me?.id, slaTick]);

  // Dedicated Bookings view — render only the dialog (full-screen feel)
  if (bookingsOnly) {
    return (
      <div className="h-full w-full bg-[var(--bg)] text-[var(--fg)]" data-testid="bookings-view">
        <BookingsDialog
          open={true}
          onClose={() => window.close()}
          defaultFilter="pending"
          fullPage={true}
        />
      </div>
    );
  }

  const activeMessages = selected ? messagesByPhone[selected] || [] : [];
  const activeContact = contacts.find((c) => c.phone === selected) || null;

  // SSE message-event safety net (per user report 2026-02-15) — on
  // production some `message` SSE events were being dropped / buffered
  // by the upstream proxy while `contact` events kept flowing (sidebar
  // updated but the open chat didn't show new bubbles until refresh).
  // Detection: when the active contact's `last_timestamp` is newer than
  // our locally-cached last message for the open chat, force-refetch
  // the chat history. Tracked per-phone via a ref so we don't loop on
  // the same timestamp. Cheap (one /conversations/{phone}/messages call
  // every time a delta is detected, never more often than ~1s realistic).
  const lastSyncedTsRef = useRef({});
  useEffect(() => {
    if (!selected || !activeContact) return;
    const contactTs = activeContact.last_timestamp || "";
    if (!contactTs) return;
    const localLast = activeMessages[activeMessages.length - 1];
    const localTs = localLast?.timestamp || "";
    // Skip when the chat is freshly opened with an empty bucket —
    // handleSelect() already triggers loadConversationHistory(phone)
    // for that case; let that initial fetch land first.
    if (!localTs) return;
    if (contactTs > localTs && lastSyncedTsRef.current[selected] !== contactTs) {
      lastSyncedTsRef.current[selected] = contactTs;
      try { loadConversationHistory(selected, { forceRefresh: true }); } catch (_) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, activeContact?.last_timestamp, activeMessages.length]);

  const [claimWarn, setClaimWarn] = useState(null); // {phone, claim}

  const handleSelect = async (phone) => {
    // Allow callers to deselect by passing null/empty (e.g. when changing
    // channel tab and the active chat doesn't belong to the new channel).
    if (!phone) {
      setSelected(null);
      return;
    }
    // Claim-lock check — if another agent has actively claimed this chat,
    // show a warning before opening so we don't accidentally double-reply.
    const claim = claimsByPhone[phone];
    const claimedByOther =
      claim && claim.user_id && me?.id && claim.user_id !== me.id;
    if (claimedByOther) {
      setClaimWarn({ phone, claim });
      return;
    }
    setSelected(phone);
    // Lazy-load full per-phone history — global /messages endpoint is
    // capped at 500 latest across all contacts, so an active chat can lose
    // its older history once the inbox grows. This guarantees the agent
    // sees every message ever exchanged with this number.
    try { loadConversationHistory(phone); } catch (_) {}
    // mark as read
    try {
      await api.patch(`/conversations/${phone}/read`);
    } catch (_) {}
  };

  const handleTakeOver = async () => {
    if (!claimWarn) return;
    const phone = claimWarn.phone;
    try {
      await api.post(`/conversations/${phone}/claim`, { take_over: true });
      toast.success("You have taken over this chat");
    } catch (_) {
      toast.error("Failed to take over");
    }
    setClaimWarn(null);
    setSelected(phone);
    try { loadConversationHistory(phone); } catch (_) {}
    try { await api.patch(`/conversations/${phone}/read`); } catch (_) {}
  };

  const injectTest = async () => {
    try {
      const phone = "1555" + Math.floor(1000000 + Math.random() * 8999999);
      await api.post("/webhook/test", {
        from: phone,
        name: `Customer ${phone.slice(-4)}`,
        text: "Hello, I need help with my order 🙂",
      });
      toast.success("Test message sent");
    } catch (_) {
      toast.error("Failed to send test");
    }
  };

  return (
    <div
      className="h-full w-full flex flex-col overflow-hidden bg-[var(--bg)] text-[var(--fg)]"
      data-testid="dashboard-root"
    >
      <StatusBar
        source={source}
        lastUpdate={lastUpdate}
        onGoHome={() => setSelected(null)}
        onInjectTest={injectTest}
        onOpenStats={() => setStatsOpen(true)}
        onOpenTeam={() => setTeamOpen(true)}
        onOpenAgentStats={() => setAgentStatsOpen(true)}
        onOpenTemplates={() => setTemplatesOpen(true)}
        onOpenShortcuts={() => setShortcutsOpen(true)}
        onOpenCampaigns={() => setCampaignsOpen(true)}
        onOpenBots={() => setBotsOpen(true)}
        onOpenBookings={() => {
          setBookingPrefill({ phone: "", name: "" });
          setBookingsOpen(true);
        }}
        onOpenDbStats={() => setDbStatsOpen(true)}
        onOpenWelcome={() => setWelcomeOpen(true)}
        onOpenPlayReviews={() => navigate("/play-reviews")}
        onNewChat={() => setNewChatOpen(true)}
      />

      <div className="flex flex-1 overflow-hidden">
        <Sidebar
          contacts={filteredContacts}
          allCount={contacts.length}
          counts={{
            all: contacts.filter((c) => !!c.last_timestamp).length,
            complaint: contacts.filter((c) => isActiveComplaint(c)).length,
            // "Call" filter — customers who tapped "Talk to an agent"/
            // "Direct Call Support"/"Talk to Support". Reset on close.
            call_request: contacts.filter(
              (c) => c.call_request && c.status !== "resolved",
            ).length,
            overdue: contacts.filter((c) => isOverdue(c, slaTick)).length,
            mine: contacts.filter(
              (c) => c.assigned_to?.id === me?.id && c.status !== "resolved",
            ).length,
            // "New" = unread (waiting for agent reply). Matches the filter
            // semantics in `filteredContacts` above.
            new: contacts.filter((c) => Number(c.unread) > 0).length,
            in_progress: contacts.filter((c) => c.status === "in_progress").length,
            resolved: contacts.filter((c) => c.status === "resolved").length,
          }}
          selected={selected}
          onSelect={handleSelect}
          search={search}
          setSearch={setSearch}
          filter={filter}
          setFilter={setFilter}
          currentUserId={me?.id}
          typingByPhone={typingByPhone}
          viewersByPhone={viewersByPhone}
          claimsByPhone={claimsByPhone}
          onSearchResults={mergeContactsList}
          mobileHidden={!!selected}
        />
        <ChatWindow
          contact={activeContact}
          messages={activeMessages}
          onBack={() => setSelected(null)}
          mobileVisible={!!selected}
          viewers={selected ? (viewersByPhone[selected] || []) : []}
          claim={selected ? (claimsByPhone[selected] || null) : null}
          othersTyping={selected ? (typingByPhone[selected] || null) : null}
          onLoadOlder={() =>
            selected ? loadConversationHistory(selected, { older: true }) : null
          }
          addOptimistic={addOptimistic}
          replaceOptimistic={replaceOptimistic}
          removeOptimistic={removeOptimistic}
          onCreateBooking={(contact) => {
            // New: opens the Agent Booking Wizard (pickup→drop→fare→date/time
            // → quotation sent to customer with one-tap confirm). Replaces
            // the legacy local booking dialog.
            setBookingWizardContact(contact);
            setBookingWizardOpen(true);
          }}
        />
      </div>

      <StatsDialog open={statsOpen} onClose={() => setStatsOpen(false)} />
      <TeamDialog open={teamOpen} onClose={() => setTeamOpen(false)} />
      <AgentStatsDialog open={agentStatsOpen} onOpenChange={setAgentStatsOpen} />
      <TemplatesDialog open={templatesOpen} onOpenChange={setTemplatesOpen} />
      <ShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
      <CampaignsDialog open={campaignsOpen} onOpenChange={setCampaignsOpen} />
      <BotsDialog open={botsOpen} onOpenChange={setBotsOpen} />
      <NewChatDialog open={newChatOpen} onClose={() => setNewChatOpen(false)} />
      <BookingsDialog
        open={bookingsOpen}
        onClose={() => {
          setBookingsOpen(false);
          setBookingPrefill({ phone: "", name: "" });
        }}
        prefillPhone={bookingPrefill.phone}
        prefillName={bookingPrefill.name}
      />
      <AgentBookingWizard
        open={bookingWizardOpen}
        onClose={() => {
          setBookingWizardOpen(false);
          setBookingWizardContact(null);
        }}
        contact={bookingWizardContact}
      />
      <DbStatsDialog open={dbStatsOpen} onOpenChange={setDbStatsOpen} />
      <WelcomeMessagesDialog open={welcomeOpen} onOpenChange={setWelcomeOpen} />

      {/* Claim-lock warning — another agent is actively handling this chat */}
      <AlertDialog open={!!claimWarn} onOpenChange={(o) => { if (!o) setClaimWarn(null); }}>
        <AlertDialogContent data-testid="claim-warning-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldWarning size={18} weight="fill" className="text-[#F59E0B]" />
              {claimWarn?.claim?.user_name || "Someone"} is handling this chat
            </AlertDialogTitle>
            <AlertDialogDescription>
              To avoid double-replies, another teammate already claimed this
              conversation. You can preview anyway or take over ownership if they're
              unavailable.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="claim-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleTakeOver}
              className="bg-[#F59E0B] hover:bg-[#D97706] text-white"
              data-testid="claim-take-over"
            >
              Take over
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
